import { computed, ref, watch } from "vue";
import { useStore } from "vuex";

import { PhoneSelectionType } from "@/enums/communicationLogs";
import { CommunicationProvider } from "@/enums/communicationProviders";
import { PhoneNumberPreferences } from "@/enums/preferences";

import type { PhoneNumberRoutingValue } from "@/models/preferences";
import type { IUser } from "@/models/users";

export const useTwilio = () => {
  const { getters } = useStore();

  const savedPhoneNumber = ref<PhoneNumberRoutingValue | null>(null);
  const preferredDialer = ref<CommunicationProvider | null>(null);

  const user = computed<IUser | null>(() => getters["auth/user"]);

  const defaultCallProvider = computed<string | null>(
    () => getters["options/defaultCallProvider"]
  );

  const effectiveDialer = computed<CommunicationProvider | null>(() => {
    if (preferredDialer.value) {
      return preferredDialer.value;
    }

    const optionsDefault = defaultCallProvider.value;
    const validProviderValues = Object.values(
      CommunicationProvider
    ) as string[];

    if (optionsDefault && validProviderValues.includes(optionsDefault)) {
      return optionsDefault as CommunicationProvider;
    }

    return null;
  });

  const savedPhoneDisplay = computed(() =>
    savedPhoneNumber.value?.type === PhoneSelectionType.Auto
      ? PhoneSelectionType.Auto
      : savedPhoneNumber.value?.phone_number || "-"
  );

  const getSavedPhoneNumber = () => {
    savedPhoneNumber.value =
      (user.value?.preferences?.find(
        ({ key }) => key === PhoneNumberPreferences.routing
      )?.value as PhoneNumberRoutingValue) ?? null;
  };

  const getPreferredDialer = () => {
    preferredDialer.value =
      (user.value?.preferences?.find(
        ({ key }) => key === PhoneNumberPreferences.provider
      )?.value as CommunicationProvider) ?? null;
  };

  watch(
    () => user.value?.preferences,
    () => {
      getSavedPhoneNumber();
      getPreferredDialer();
    },
    {
      immediate: true,
      deep: true,
    }
  );

  return {
    savedPhoneNumber,
    savedPhoneDisplay,
    preferredDialer,
    effectiveDialer,
  };
};
